import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Eye, MessageSquare, Search } from 'lucide-react';
import { portalApi } from '@/services/portal-api';
import { StatusBadge } from '@/components/portal/StatusBadge';

interface ApreciacaoItem {
  compartilhamentoId: string;
  status: string;
  dataCompartilhamento: string;
  dataResposta: string | null;
  totalAnotacoes: number;
  apreciacao: {
    id: string;
    numero: string;
    titulo: string;
    revisao: number;
    dataEmissao: string;
    maquina: { id: string; nome: string; tipo: string };
    projeto: { id: string; nome: string };
  };
}

export function PortalApreciacoesList() {
  const [apreciacoes, setApreciacoes] = useState<ApreciacaoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [busca, setBusca] = useState('');

  useEffect(() => {
    portalApi.listarApreciacoes().then(res => {
      if (res.success && res.data) {
        setApreciacoes(res.data);
      }
      setLoading(false);
    });
  }, []);

  const filtered = apreciacoes.filter(item => {
    if (filtroStatus !== 'todos' && item.status !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (
        item.apreciacao.numero.toLowerCase().includes(q) ||
        item.apreciacao.titulo.toLowerCase().includes(q) ||
        item.apreciacao.maquina.nome.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Apreciacoes de Risco</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Documentos compartilhados para sua revisao
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por numero, titulo ou maquina..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-1">
          {[
            { key: 'todos', label: 'Todos' },
            { key: 'pendente', label: 'Pendentes' },
            { key: 'aprovado', label: 'Aprovados' },
            { key: 'revisao_solicitada', label: 'Revisao' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFiltroStatus(f.key)}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                filtroStatus === f.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>Nenhuma apreciacao encontrada</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3">Numero</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3">Titulo</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3 hidden md:table-cell">Maquina</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3 hidden lg:table-cell">Rev.</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3 hidden lg:table-cell">Compartilhado em</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3 hidden md:table-cell">Anotacoes</th>
                  <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.compartilhamentoId} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{item.apreciacao.numero}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900 dark:text-white">{item.apreciacao.titulo}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.apreciacao.maquina.nome}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.apreciacao.revisao}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-xs text-gray-500">{formatDate(item.dataCompartilhamento)}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <MessageSquare className="w-3.5 h-3.5" />
                        {item.totalAnotacoes}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/portal/apreciacoes/${item.apreciacao.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Revisar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
